import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  Globe,
  History,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Send,
  TimerReset,
  X,
  XCircle,
  MoreVertical,
} from 'lucide-react';
import {
  assignSubmissionIssue,
  createIssue,
  getAdminDashboard,
  getStoredAuth,
  getSubmission,
  listAdminSubmissions,
  listIssues,
  publishIssue,
  unpublishIssue,
  publishSubmission,
  updateSubmissionStatus,
} from '../lib/auth';
import Badge from '../components/ui/Badge.jsx';

const statuses = {
  submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-800', icon: Send },
  'under-review': { label: 'Under Review', className: 'bg-amber-tint text-amber-tinttext', icon: Clock3 },
  'revision-required': { label: 'Revision Required', className: 'bg-orange-100 text-orange-800', icon: TimerReset },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800', icon: XCircle },
  draft: { label: 'Draft', className: 'bg-bg-tag text-slate', icon: FileText },
  withdrawn: { label: 'Withdrawn', className: 'bg-gray-100 text-gray-700', icon: FileText },
  published: { label: 'Published', className: 'bg-purple-100 text-purple-800', icon: Globe },
};

const decisions = [
  { key: 'under-review', label: 'Set to Review', icon: Clock3, tone: 'border-blue-200 text-blue-700 hover:bg-blue-50' },
  { key: 'request-revision', label: 'Request Revision', icon: TimerReset, tone: 'border-orange-200 text-orange-700 hover:bg-orange-50' },
  { key: 'accept', label: 'Accept', icon: CheckCircle2, tone: 'border-green-200 text-green-700 hover:bg-green-50' },
  { key: 'reject', label: 'Reject', icon: XCircle, tone: 'border-red-200 text-red-700 hover:bg-red-50' },
  { key: 'publish', label: 'Publish', icon: Globe, tone: 'border-purple-200 text-purple-700 hover:bg-purple-50' },
];

function formatDate(value) {
  if (!value) return 'Not available';
  return new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
}

function documentUrl(file) {
  return file?.publicUrl || file?.url;
}

function fileName(file, fallback) {
  return file?.filename || file?.name || fallback;
}

function AdminDashboard() {
  const auth = getStoredAuth();
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [issues, setIssues] = useState([]);
  const [selectedIssues, setSelectedIssues] = useState({});
  const [issueForm, setIssueForm] = useState({
    volume: '',
    issueNumber: '',
    season: 'Spring',
    year: new Date().getFullYear(),
    title: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [decisionModal, setDecisionModal] = useState(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionImage, setDecisionImage] = useState(null);
  const [issueImage, setIssueImage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [dashboard, list, issueList] = await Promise.all([getAdminDashboard(), listAdminSubmissions(), listIssues()]);
      setStats(dashboard.data);
      const submissionList = list.data || [];
      setSubmissions(submissionList);
      setIssues(issueList.data || []);
      // Seed selectedIssues from each submission's assignedIssue (only for unlocked submissions)
      setSelectedIssues((prev) => {
        const next = { ...prev };
        submissionList.forEach((sub) => {
          if (sub.assignedIssue && sub.status !== 'published') {
            next[sub._id] = sub.assignedIssue._id;
          }
        });
        return next;
      });
      setMessage('');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Editorial Dashboard - JAMSD';
    if (auth?.token) load();
  }, [auth?.token]);

  const filtered = useMemo(() => submissions.filter((item) => {
    const haystack = `${item.submissionId || ''} ${item.title || ''} ${item.correspondingAuthor?.fullName || ''} ${item.correspondingAuthor?.email || ''}`.toLowerCase();
    return (!statusFilter || item.status === statusFilter) && haystack.includes(query.toLowerCase());
  }), [submissions, statusFilter, query]);

  const openSubmission = async (id) => {
    setDetailLoading(true);
    setMessage('');
    try {
      const result = await getSubmission(id);
      const sub = result.data;
      setSelected(sub);
      // Seed selectedIssues from the submission's assignedIssue if not already set
      if (sub.assignedIssue && sub.status !== 'published') {
        setSelectedIssues((prev) => ({
          ...prev,
          [sub._id]: sub.assignedIssue._id,
        }));
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDecision = (submission, decision) => {
    setDecisionComment('');
    setDecisionImage(null);
    setDecisionModal({ submission, decision });
  };

  const decide = async () => {
    try {
      const { submission, decision } = decisionModal;
      const issue = selectedIssues[submission._id];

      if (decision === 'publish') {
        if (!issue) {
          setMessage('Select a volume and issue before publishing this manuscript.');
          return;
        }
        await publishSubmission(submission._id, { issue }, decisionImage);
      } else {
        if (decision !== 'under-review' && !decisionComment.trim()) {
          setMessage('Decision comments are required for accept, reject, and revision decisions.');
          return;
        }
        await updateSubmissionStatus(submission._id, {
          decision,
          comments: decisionComment.trim(),
          issue,
        });
      }

      setDecisionModal(null);
      setDecisionComment('');
      setDecisionImage(null);
      await load();
      if (selected?._id === submission._id) await openSubmission(submission._id);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const toggleIssuePublish = async (issueId, currentlyPublished) => {
    try {
      if (currentlyPublished) {
        await unpublishIssue(issueId);
      } else {
        await publishIssue(issueId);
      }
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  // Saves issue assignment to the server immediately on select (admin only, before publish)
  const handleSelectIssue = async (submissionId, issueId) => {
    setSelectedIssues((prev) => ({ ...prev, [submissionId]: issueId }));
    try {
      await assignSubmissionIssue(submissionId, issueId || null);
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleIssueChange = (event) => {
    const { name, value } = event.target;
    setIssueForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitIssue = async (event) => {
    event.preventDefault();
    try {
      await createIssue(issueForm, issueImage);
      setIssueForm({
        volume: '',
        issueNumber: '',
        season: 'Spring',
        year: new Date().getFullYear(),
        title: '',
      });
      setIssueImage(null);
      setMessage('Volume and issue created.');
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  if (!auth?.token) return <div className="max-w-4xl mx-auto px-4 pt-28 pb-16">Please sign in.</div>;

  return (
    <>
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest">Editorial Workflow</p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mt-2">Admin Dashboard</h1>
              <p className="font-sans text-slate-light mt-3 max-w-2xl">Review submissions, inspect files and audit logs, then record editorial actions with comments.</p>
            </div>
            <button type="button" onClick={load} className="btn-amber inline-flex items-center justify-center gap-2 px-5 py-3">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {message && <div className="mb-5 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <Metric label="Users" value={stats?.userCount || 0} icon={BarChart3} />
          <Metric label="Published" value={stats?.publishedArticleCount || 0} icon={FileText} />
          {['submitted', 'under-review', 'revision-required', 'accepted'].map((key) => {
            const config = statuses[key];
            return <Metric key={key} label={config.label} value={stats?.submissionCounts?.[key] || 0} icon={config.icon} active={statusFilter === key} onClick={() => setStatusFilter(statusFilter === key ? '' : key)} />;
          })}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-8 items-start">
          <section className="space-y-5">
            <div className="rounded-lg border border-bg-border bg-white p-4 shadow-sm">
              <div className="flex flex-col md:flex-row gap-3">
                <label className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate" />
                  <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search submissions, authors, or emails" className="w-full rounded border border-bg-border py-2.5 pl-10 pr-3 text-sm" />
                </label>
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded border border-bg-border px-3 py-2.5 text-sm md:w-56">
                  <option value="">All statuses</option>
                  {Object.entries(statuses).map(([key, config]) => <option key={key} value={key}>{config.label}</option>)}
                </select>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-bg-border bg-white shadow-sm">
              <div className="border-b border-bg-border bg-bg-ice px-4 py-3">
                <h2 className="font-display text-xl font-bold text-navy">Submissions</h2>
                <p className="text-sm text-slate">{filtered.length} manuscript{filtered.length === 1 ? '' : 's'} in view</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-left font-sans text-sm">
                  <thead className="bg-white text-xs uppercase tracking-wide text-slate">
                    <tr>
                      <th className="px-4 py-3">Submission</th>
                      <th className="px-4 py-3">Author</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Issue</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-slate">Loading submissions...</td></tr>
                    ) : filtered.map((item) => (
                      <SubmissionRow
                        key={item._id}
                        item={item}
                        issues={issues}
                        selectedIssue={selectedIssues[item._id] || ''}
                        onSelectIssue={(value) => handleSelectIssue(item._id, value)}
                        onOpen={() => openSubmission(item._id)}
                        onDecision={(decision) => openDecision(item, decision)}
                      />
                    ))}
                    {!loading && !filtered.length && (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-slate">No submissions match the current filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <aside className="space-y-5 xl:sticky xl:top-24">
            {['admin', 'superadmin'].includes(auth.user.role) && (
              <form onSubmit={submitIssue} className="rounded-lg border border-bg-border bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <Plus className="h-5 w-5 text-amber" />
                  <h2 className="font-display text-xl font-bold text-navy">Create Issue</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input name="volume" value={issueForm.volume} onChange={handleIssueChange} placeholder="Volume" className="rounded border border-bg-border px-3 py-2" required />
                  <input name="issueNumber" value={issueForm.issueNumber} onChange={handleIssueChange} placeholder="Issue" className="rounded border border-bg-border px-3 py-2" required />
                  <input name="year" value={issueForm.year} onChange={handleIssueChange} placeholder="Year" className="rounded border border-bg-border px-3 py-2" required />
                  <select name="season" value={issueForm.season} onChange={handleIssueChange} className="rounded border border-bg-border px-3 py-2">
                    <option>Spring</option>
                    <option>Fall</option>
                    <option>Special</option>
                  </select>
                  <input name="title" value={issueForm.title} onChange={handleIssueChange} placeholder="Optional title" className="col-span-2 rounded border border-bg-border px-3 py-2" />
                </div>
                <label className="mt-3 block text-xs font-semibold text-navy">
                  Cover Image (optional)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/tiff"
                    onChange={(event) => setIssueImage(event.target.files?.[0] || null)}
                    className="mt-1 w-full rounded border border-bg-border px-3 py-2 text-sm"
                  />
                  {issueImage && <span className="mt-1 block text-xs text-slate">{issueImage.name}</span>}
                </label>
                <button type="submit" className="btn-amber mt-4 w-full px-5 py-2">Create Issue</button>
              </form>
            )}

            {issues.length > 0 && (
              <div className="rounded-lg border border-bg-border bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-amber" />
                  <h2 className="font-display text-xl font-bold text-navy">Issues</h2>
                </div>
                <div className="space-y-2">
                  {issues.map((issue) => (
                    <div key={issue._id} className="flex items-center justify-between rounded border border-bg-border bg-bg-ice px-3 py-2">
                      <div>
                        <p className="font-sans text-sm font-semibold text-navy">Vol. {issue.volume} · Issue {issue.issueNumber}</p>
                        <p className="text-xs text-slate">{issue.season} {issue.year}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleIssuePublish(issue._id, issue.isPublished)}
                        className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${issue.isPublished ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-bg-tag text-slate hover:bg-navy hover:text-white'}`}
                      >
                        {issue.isPublished ? 'Published' : 'Publish'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border border-bg-border bg-white p-5 shadow-sm">
              <h2 className="font-display text-xl font-bold text-navy">Recent Activity</h2>
              <div className="mt-4 space-y-3">
                {(stats?.recentSubmissions || []).map((item) => (
                  <button key={item._id} type="button" onClick={() => openSubmission(item._id)} className="block w-full rounded border border-bg-border bg-bg-ice p-3 text-left hover:border-amber">
                    <p className="text-xs uppercase text-slate">{item.submissionId || 'Draft'}</p>
                    <p className="line-clamp-2 font-semibold text-navy">{item.title || 'Untitled manuscript'}</p>
                    <p className="mt-1 text-xs text-slate">{formatDate(item.createdAt)}</p>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>

      {detailLoading && <Modal title="Loading Submission" onClose={() => setDetailLoading(false)}><p className="text-sm text-slate">Fetching submission details...</p></Modal>}
      {selected && (
        <SubmissionDetail
          submission={selected}
          issues={issues}
          selectedIssue={selectedIssues[selected._id] || ''}
          onSelectIssue={(value) => handleSelectIssue(selected._id, value)}
          onClose={() => setSelected(null)}
          onDecision={(decision) => openDecision(selected, decision)}
        />
      )}
      {decisionModal && (
        <DecisionModal
          modal={decisionModal}
          comment={decisionComment}
          setComment={setDecisionComment}
          image={decisionImage}
          setImage={setDecisionImage}
          issues={issues}
          selectedIssue={selectedIssues[decisionModal.submission._id] || ''}
          onSelectIssue={(value) => handleSelectIssue(decisionModal.submission._id, value)}
          onClose={() => setDecisionModal(null)}
          onSubmit={decide}
        />
      )}
    </>
  );
}

function Metric({ label, value, icon: Icon, active, onClick }) {
  const content = (
    <>
      <Icon className="mb-3 h-5 w-5 text-amber" />
      <p className="font-display text-3xl font-bold text-navy">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-wide text-slate">{label}</p>
    </>
  );
  const className = `rounded-lg border bg-white p-4 text-left shadow-sm ${active ? 'border-amber ring-2 ring-amber/20' : 'border-bg-border'}`;
  return onClick ? <button type="button" onClick={onClick} className={className}>{content}</button> : <div className={className}>{content}</div>;
}

// function SubmissionRow({ item, issues, selectedIssue, onSelectIssue, onOpen, onDecision }) {
//   const status = statuses[item.status] || statuses.submitted;
//   const [menuOpen, setMenuOpen] = useState(false);
//   return (
//     <tr className="border-t border-bg-border align-top hover:bg-bg-ice/50">
//       <td className="px-4 py-4">
//         <button type="button" onClick={onOpen} className="text-left">
//           <strong className="text-navy">{item.submissionId || 'Draft'}</strong>
//           <p className="mt-1 line-clamp-2 text-slate">{item.title || 'Untitled manuscript'}</p>
//           <div className="mt-2"><Badge type={item.articleType} /></div>
//         </button>
//       </td>
//       <td className="px-4 py-4">
//         <p className="font-semibold text-navy">{item.correspondingAuthor?.fullName || 'Unknown'}</p>
//         <p className="text-xs text-slate">{item.correspondingAuthor?.email}</p>
//       </td>
//       <td className="px-4 py-4">
//         <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}>{status.label}</span>
//       </td>
//       <td className="px-4 py-4">
//         {item.issue ? (
//           <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
//             Vol. {item.issue.volume} · Issue {item.issue.issueNumber}
//           </p>
//         ) : (
//           <select value={selectedIssue} onChange={(event) => onSelectIssue(event.target.value)} className="w-full min-w-[170px] rounded border border-bg-border px-3 py-2 text-sm">
//             <option value="">Select issue</option>
//             {issues.map((issue) => (
//               <option key={issue._id} value={issue._id}>
//                 Vol. {issue.volume} - Issue {issue.issueNumber} ({issue.year})
//               </option>
//             ))}
//           </select>
//         )}
//       </td>
//       <td className="px-4 py-4">
//         <div className="flex flex-wrap gap-2">
//           <button type="button" onClick={onOpen} className="rounded border border-bg-border px-3 py-1 text-navy hover:border-amber">View</button>
//           {decisions.map((decision) => {
//             const Icon = decision.icon;
//             return (
//               <button key={decision.key} type="button" onClick={() => onDecision(decision.key)} className={`inline-flex items-center gap-1 rounded border px-3 py-1 ${decision.tone}`}>
//                 <Icon className="h-3.5 w-3.5" />
//                 {decision.label}
//               </button>
//             );
//           })}
//         </div>
//       </td>
//     </tr>
//   );
// }

/**
 * IssueCell — shows a locked badge when published, an editable dropdown otherwise.
 * The assigned issue (from server) is shown even before publishing.
 */
function IssueCell({ item, issues, selectedIssue, onSelectIssue, wide = false }) {
  const isPublished = item.status === 'published';
  const assignedIssue = item.assignedIssue;

  if (isPublished) {
    const vol = assignedIssue?.volume ?? item.volume;
    const iss = assignedIssue?.issueNumber ?? item.issueNumber;
    return (
      <div className="rounded border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 flex items-center gap-1.5">
        <Globe className="h-3.5 w-3.5 flex-none" />
        {vol && iss ? `Vol. ${vol} · Issue ${iss}` : 'Published'}
      </div>
    );
  }

  return (
    <div className={wide ? '' : 'min-w-[170px]'}>
      {selectedIssue && (
        <p className="mb-1 text-xs text-green-700 font-semibold flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 flex-none" /> Assigned — changeable until published
        </p>
      )}
      <select
        value={selectedIssue}
        onChange={(event) => onSelectIssue(event.target.value)}
        className={`w-full rounded border px-3 py-2 text-sm ${selectedIssue ? 'border-green-300 bg-green-50' : 'border-bg-border bg-white'}`}
      >
        <option value="">— Select issue —</option>
        {issues.map((issue) => (
          <option key={issue._id} value={issue._id}>
            {issue.isPublished ? '✓ ' : ''}Vol. {issue.volume} · Issue {issue.issueNumber} ({issue.year})
          </option>
        ))}
      </select>
    </div>
  );
}

function SubmissionRow({ item, issues, selectedIssue, onSelectIssue, onOpen, onDecision }) {
  const status = statuses[item.status] || statuses.submitted;
  const [menuOpen, setMenuOpen] = useState(false);
  const visibleDecisions = decisions.filter((d) => {
    if (d.key === 'publish') return item.status === 'accepted';
    if (['published', 'rejected', 'withdrawn'].includes(item.status)) return false;
    return true;
  });

  return (
    <tr className="border-t border-bg-border align-top hover:bg-bg-ice/50">
      <td className="px-4 py-4">
        <button type="button" onClick={onOpen} className="text-left">
          <strong className="text-navy">{item.submissionId || 'Draft'}</strong>
          <p className="mt-1 line-clamp-2 text-slate">{item.title || 'Untitled manuscript'}</p>
          <div className="mt-2"><Badge type={item.articleType} /></div>
        </button>
      </td>

      <td className="px-4 py-4">
        <p className="font-semibold text-navy">{item.correspondingAuthor?.fullName || 'Unknown'}</p>
        <p className="text-xs text-slate">{item.correspondingAuthor?.email}</p>
      </td>

      <td className="px-4 py-4">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}>
          {status.label}
        </span>
      </td>

      <td className="px-4 py-4">
        <IssueCell
          item={item}
          issues={issues}
          selectedIssue={selectedIssue}
          onSelectIssue={onSelectIssue}
        />
      </td>

      <td className="relative px-4 py-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="rounded border border-bg-border px-3 py-1 text-navy hover:border-amber"
          >
            View
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="rounded border border-bg-border p-2 hover:border-amber hover:bg-bg-ice"
          >
            <MoreVertical className="h-4 w-4 text-navy" />
          </button>
        </div>

        {menuOpen && (
          <div className="absolute right-4 top-14 z-20 w-56 rounded-lg border border-bg-border bg-white shadow-lg">
            {visibleDecisions.map((decision) => {
              const Icon = decision.icon;

              return (
                <button
                  key={decision.key}
                  type="button"
                  onClick={() => {
                    onDecision(decision.key);
                    setMenuOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-3 text-left text-sm hover:bg-bg-ice ${decision.tone}`}
                >
                  <Icon className="h-4 w-4" />
                  {decision.label}
                </button>
              );
            })}
          </div>
        )}
      </td>
    </tr>
  );
}

function SubmissionDetail({ submission, issues, selectedIssue, onSelectIssue, onClose, onDecision }) {
  const status = statuses[submission.status] || statuses.submitted;
  const visibleDecisions = decisions.filter((d) => {
    if (d.key === 'publish') return submission.status === 'accepted';
    if (['published', 'rejected', 'withdrawn'].includes(submission.status)) return false;
    return true;
  });
  return (
    <Modal title={submission.submissionId || 'Draft manuscript'} onClose={onClose} wide>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}>{status.label}</span>
            <h2 className="mt-3 font-display text-2xl font-bold text-navy">{submission.title || 'Untitled manuscript'}</h2>
            <p className="mt-2 text-sm text-slate">{submission.correspondingAuthor?.fullName} · {submission.correspondingAuthor?.email}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {visibleDecisions.map((decision) => {
              const Icon = decision.icon;
              return (
                <button key={decision.key} type="button" onClick={() => onDecision(decision.key)} className={`inline-flex items-center justify-center gap-2 rounded border px-3 py-2 text-sm font-semibold ${decision.tone}`}>
                  <Icon className="h-4 w-4" />
                  {decision.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InfoPanel title="Submission Details">
            <p className="text-xs uppercase text-slate">Article Type</p>
            <p className="mb-3 font-semibold text-navy">{String(submission.articleType || '').replace(/-/g, ' ')}</p>
            <p className="text-xs uppercase text-slate">Abstract</p>
            <div className="text-sm text-slate" dangerouslySetInnerHTML={{ __html: submission.abstract || 'No abstract provided.' }} />
            <p className="mt-3 text-xs uppercase text-slate">Keywords</p>
            <p className="text-sm text-navy">{(submission.keywords || []).join(', ') || 'Not provided'}</p>
          </InfoPanel>
          <InfoPanel title="Documents">
            <DocumentLink file={submission.manuscriptFile} label="Manuscript" />
            <DocumentLink file={submission.coverPageFile} label="Cover Letter" />
            {(submission.revisions || []).map((revision, index) => (
              revision.replyToReviewerFile ? <DocumentLink key={index} file={revision.replyToReviewerFile} label={`Reply to Reviewer (Revision ${index + 1})`} /> : null
            ))}
            <div className="mt-4">
              <p className="mb-2 text-xs uppercase text-slate">Assign to Issue</p>
              <IssueCell
                item={submission}
                issues={issues}
                selectedIssue={selectedIssue}
                onSelectIssue={onSelectIssue}
                wide
              />
            </div>
          </InfoPanel>
        </div>

        <InfoPanel title="Authors">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(submission.authors || []).map((author, index) => (
              <div key={`${author.email}-${index}`} className="rounded border border-bg-border bg-bg-ice p-3">
                <p className="font-semibold text-navy">{author.name || 'Unnamed author'} {author.isCorresponding ? <span className="text-xs text-amber">Corresponding</span> : null}</p>
                <p className="text-sm text-slate">{author.email}</p>
                <p className="text-xs text-gray-500">{author.affiliation}</p>
              </div>
            ))}
          </div>
        </InfoPanel>

        <InfoPanel title="Suggested Peer Reviewers">
          <p className="mb-2 text-xs uppercase text-slate">Suggested Reviewers</p>
          {(submission.suggestedReviewers || []).filter((r) => r.name || r.email).length ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {submission.suggestedReviewers.map((reviewer, index) => (
                <div key={`${reviewer.email}-${index}`} className="rounded border border-bg-border bg-bg-ice p-3">
                  <p className="font-semibold text-navy">{reviewer.name || 'Unnamed reviewer'}</p>
                  <p className="text-sm text-slate">{reviewer.email}</p>
                  <p className="text-xs text-gray-500">{reviewer.affiliation}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate">Not provided</p>
          )}
        </InfoPanel>

        <InfoPanel title="Audit Logs">
          <AuditLog entries={submission.auditLog || []} />
        </InfoPanel>
      </div>
    </Modal>
  );
}

function DecisionModal({ modal, comment, setComment, image, setImage, issues, selectedIssue, onSelectIssue, onClose, onSubmit }) {
  const decision = decisions.find((item) => item.key === modal.decision);
  const isPublish = modal.decision === 'publish';
  return (
    <Modal title={decision?.label || 'Editorial Decision'} onClose={onClose}>
      <div className="space-y-4">
        <div className="rounded border border-bg-border bg-bg-ice p-3">
          <p className="text-xs uppercase text-slate">{modal.submission.submissionId || 'Draft'}</p>
          <p className="font-semibold text-navy">{modal.submission.title || 'Untitled manuscript'}</p>
        </div>
        {isPublish ? (
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-semibold text-navy">Select Volume &amp; Issue *</p>
              <select
                value={selectedIssue}
                onChange={(event) => onSelectIssue(event.target.value)}
                className="w-full rounded border border-bg-border px-3 py-2 text-sm"
              >
                <option value="">— Choose an issue —</option>
                {issues.map((issue) => (
                  <option key={issue._id} value={issue._id}>
                    {issue.isPublished ? '✓ ' : '⚠ '}Vol. {issue.volume} · Issue {issue.issueNumber} ({issue.year}){!issue.isPublished ? ' — not published yet' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate">Only issues marked ✓ (Published) can be selected. Publish the issue first if needed.</p>
            </div>
            <label className="block text-xs font-semibold text-navy">
              Article Cover Image (optional)
              <input
                type="file"
                accept="image/png,image/jpeg,image/tiff"
                onChange={(event) => setImage(event.target.files?.[0] || null)}
                className="mt-1 w-full rounded border border-bg-border px-3 py-2 text-sm"
              />
              {image && <span className="mt-1 block text-xs text-slate">{image.name}</span>}
              <span className="mt-1 block text-xs text-slate">Shown on the article card and detail page. If skipped, a themed thumbnail is used.</span>
            </label>
          </div>
        ) : (
          <label className="block text-xs font-semibold text-navy">Comment
            <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={5} placeholder="Write editorial comments..." className="mt-1 w-full rounded border border-bg-border px-3 py-2 text-sm" />
          </label>
        )}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded border border-bg-border px-4 py-2">Cancel</button>
          <button type="button" onClick={onSubmit} className="btn-amber px-5 py-2">{isPublish ? 'Publish' : 'Submit'}</button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
      <div className={`max-h-[90vh] w-full overflow-y-auto rounded-lg bg-white shadow-2xl ${wide ? 'max-w-6xl' : 'max-w-lg'}`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-bg-border bg-white px-5 py-4">
          <h3 className="font-display text-xl font-bold text-navy">{title}</h3>
          <button type="button" onClick={onClose} className="rounded p-2 text-slate hover:bg-bg-ice" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function InfoPanel({ title, children }) {
  return (
    <section className="rounded border border-bg-border bg-white p-4">
      <h3 className="mb-3 font-sans text-xs font-bold uppercase tracking-wide text-slate">{title}</h3>
      {children}
    </section>
  );
}

function DocumentLink({ file, label }) {
  const url = documentUrl(file);
  return (
    <div className="mb-3 rounded border border-bg-border bg-bg-ice p-3 last:mb-0">
      <p className="text-xs uppercase text-slate">{label}</p>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-2 font-semibold text-navy hover:text-amber">
          <FileText className="h-4 w-4" />
          {fileName(file, label)}
          <ExternalLink className="h-4 w-4" />
        </a>
      ) : (
        <p className="mt-1 text-sm text-slate">No file uploaded.</p>
      )}
    </div>
  );
}

function AuditLog({ entries }) {
  if (!entries.length) return <p className="text-sm text-slate">No audit activity recorded.</p>;
  return (
    <div className="space-y-3">
      {entries.slice().reverse().map((entry, index) => (
        <div key={`${entry.createdAt}-${index}`} className="flex gap-3 rounded border border-bg-border bg-bg-ice p-3">
          <History className="h-5 w-5 flex-none text-amber" />
          <div>
            <p className="text-sm font-semibold capitalize text-navy">{String(entry.action || 'activity').replace(/-/g, ' ')}</p>
            <p className="text-xs text-slate">
              {formatDate(entry.createdAt)} · {entry.actor?.fullName || 'System'}
              {entry.fromStatus || entry.toStatus ? ` · ${entry.fromStatus || 'new'} to ${entry.toStatus || 'current'}` : ''}
            </p>
            {entry.comments && (
              <p className="mt-1 flex gap-2 text-sm text-gray-700">
                <MessageSquare className="mt-0.5 h-4 w-4 flex-none text-amber" />
                {entry.comments}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default AdminDashboard;
